import {
    normalizePartner,
    chooseRecommendedPricingOffer,
    monthlyFromOffer,
    termAprFromOffer,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/utils/finance';
import quoteServices, { type PricingPayload } from '../../services/oneButtonServices/quoteServices';
import { countIncludedModules, estimateLayoutAnnualKwh } from './preContractModuleLayout';
import type { PreContractIntakeForm, PreContractProduct } from './preContractTypes';

export { normalizePartner };

export type PreContractPricingOffer = {
    id: string;
    partner: string;
    productType: string;
    financeMethod: string;
    monthly: number;
    isCash: boolean;
    term: number | null;
    apr: number | null;
    error?: string;
};

/** Partner card with one line per selected product. */
export type PreContractPartnerPricingCard = {
    partner: string;
    lines: PreContractPricingOffer[];
    /** Sum of line amounts (monthly for financed, total for Cash). */
    total: number;
    isCash: boolean;
    /** Selected products with no priced line for this partner. */
    missingProducts: string[];
};

export type PreContractPricingResult = {
    offers: PreContractPricingOffer[];
    partnerCards: PreContractPartnerPricingCard[];
    selectedProducts: string[];
    payload: PricingPayload;
};

function parseUsageKwh(raw: string): number {
    const n = Number(String(raw).replace(/,/g, '').trim());
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Prefer editable system usage; fall back to layout estimate. */
export function preContractSystemUsageKwh(form: PreContractIntakeForm): number {
    const fromForm = parseUsageKwh(form.systemUsageKwh || '');
    if (fromForm > 0) return fromForm;
    const estimated = estimateLayoutAnnualKwh({
        layouts: form.roofModuleLayouts,
        roofSelections: form.roofSelections,
        moduleWattage: form.moduleTypeWattage,
    });
    return estimated != null && estimated > 0 ? estimated : 0;
}

function activeProducts(form: PreContractIntakeForm): string[] {
    const order: PreContractProduct[] = ['Solar', 'Roof', 'Battery', 'RR', 'RRR'];
    return order.filter((p) => form.products[p]);
}

/** System size (kW) from included modules × wattage. */
export function preContractSystemSizeKw(form: PreContractIntakeForm): number {
    const wattage = form.moduleTypeWattage;
    if (wattage == null || !Number.isFinite(wattage) || wattage <= 0) return 0;
    const { count } = countIncludedModules(form.roofModuleLayouts, form.roofSelections);
    if (count <= 0) return 0;
    return Math.round(((count * wattage) / 1000) * 1000) / 1000;
}

export type PreContractPricingInputStatus = {
    key: string;
    label: string;
    value: string;
    ok: boolean;
};

function effectiveRoofSquares(form: PreContractIntakeForm): { low: number; normal: number; total: number } {
    const low = Math.round(form.roofSlopeLowOverride ?? form.roofSlopeLowTotal ?? 0);
    const normal = Math.round(form.roofSlopeNormalOverride ?? form.roofSlopeNormalTotal ?? 0);
    return { low, normal, total: low + normal };
}

export type PreContractPricingScope = 'all' | 'roofBattery';

function scopedProducts(form: PreContractIntakeForm, scope: PreContractPricingScope): string[] {
    if (scope === 'roofBattery') {
        return (['Roof', 'Battery'] as const).filter((product) => form.products[product]);
    }
    return activeProducts(form);
}

/** Snapshot of Pricing API inputs and which ones are still missing. */
export function getPreContractPricingInputStatus(
    form: PreContractIntakeForm,
    scope: PreContractPricingScope = 'all',
): {
    inputs: PreContractPricingInputStatus[];
    missing: string[];
    ready: boolean;
} {
    const state = (form.installState || '').trim().toUpperCase();
    const utilityCompanyCode = (form.utilityCompany || '').trim();
    const systemUsageKwh = preContractSystemUsageKwh(form);
    const systemSize = preContractSystemSizeKw(form);
    const products = scopedProducts(form, scope);
    const roofs = effectiveRoofSquares(form);
    const needsSolar =
        scope !== 'roofBattery' &&
        (products.some((p) => p.toLowerCase().startsWith('solar')) || products.length === 0);
    const needsRoof = products.some((p) => {
        const lower = p.toLowerCase();
        return lower === 'roof' || lower === 'rr' || lower === 'rrr';
    });

    const inputs: PreContractPricingInputStatus[] = [
        {
            key: 'state',
            label: 'State',
            value: state || '—',
            ok: !!state,
        },
        {
            key: 'utilityCompanyCode',
            label: 'Utility company code',
            value: utilityCompanyCode || '—',
            ok: !!utilityCompanyCode,
        },
        {
            key: 'estimatedAnnualProduction',
            label: 'Estimated annual production (System Usage)',
            value: systemUsageKwh > 0 ? `${systemUsageKwh.toLocaleString()} kWh` : '—',
            ok: !needsSolar || systemUsageKwh > 0,
        },
        {
            key: 'systemSize',
            label: 'System size',
            value: systemSize > 0 ? `${systemSize.toFixed(2)} kW` : '—',
            ok: !needsSolar || systemSize > 0,
        },
        {
            key: 'products',
            label: 'Products',
            value: products.length > 0 ? products.join(', ') : '—',
            ok: products.length > 0,
        },
        {
            key: 'roofSquares',
            label: 'Roof squares (Low / Normal)',
            value: `${roofs.low} / ${roofs.normal}`,
            ok: !needsRoof || roofs.total > 0,
        },
        {
            key: 'batteryCount',
            label: 'Battery count',
            value: form.products.Battery ? String(Math.max(0, Math.round(form.batteryQuantity || 0))) : '0',
            ok: !form.products.Battery || (form.batteryQuantity || 0) > 0,
        },
        {
            key: 'moduleType',
            label: 'Module type / wattage',
            value:
                form.moduleTypeName && form.moduleTypeWattage
                    ? `${form.moduleTypeName} (${form.moduleTypeWattage} W)`
                    : '—',
            ok: !needsSolar || (!!form.moduleTypeWattage && form.moduleTypeWattage > 0),
        },
    ];

    const missing = inputs.filter((i) => !i.ok).map((i) => i.label);
    return { inputs, missing, ready: missing.length === 0 };
}

/**
 * Build Pricing API payload for pre-contract.
 * Uses System Usage as estimatedAnnualProduction.
 */
export function buildPreContractPricingPayload(
    form: PreContractIntakeForm,
    scope: PreContractPricingScope = 'all',
): PricingPayload | null {
    const { ready } = getPreContractPricingInputStatus(form, scope);
    if (!ready) return null;

    const state = (form.installState || '').trim().toUpperCase();
    const systemUsageKwh = preContractSystemUsageKwh(form);
    const systemSize = preContractSystemSizeKw(form);
    const products = scopedProducts(form, scope);
    const utilityCompanyCode = (form.utilityCompany || '').trim();
    const roofs = effectiveRoofSquares(form);
    const roofPitch =
        roofs.normal > 0 && roofs.low === 0
            ? 'Normal Slope'
            : roofs.low > 0
              ? 'Low Slope'
              : 'Normal Slope';

    return {
        state,
        systemSize,
        estimatedAnnualProduction: Math.round(systemUsageKwh),
        utilityCompanyCode,
        batteryCount: form.products.Battery ? Math.max(0, Math.round(form.batteryQuantity || 0)) : 0,
        roofPitch,
        lowRoof: roofs.low,
        standardRoof: roofs.normal,
        products: products.length > 0 ? products : ['Solar'],
    };
}

/** Map API product type onto a selected product key when possible. */
function matchSelectedProduct(productType: string, selected: string[]): string | null {
    const pt = String(productType || '')
        .trim()
        .toLowerCase();
    if (!pt) return null;

    const find = (pred: (sel: string) => boolean) => selected.find(pred) ?? null;

    if (pt.includes('solar')) return find((s) => s.toLowerCase() === 'solar');
    if (pt.includes('battery') || pt.includes('storage')) {
        return find((s) => s.toLowerCase() === 'battery');
    }
    if (pt === 'rrr' || pt.includes('roof replacement')) {
        return find((s) => s.toLowerCase() === 'rrr');
    }
    if (pt === 'rr' || pt.includes('roof repair')) {
        return find((s) => s.toLowerCase() === 'rr');
    }
    if (pt === 'roof' || pt.includes('roof')) {
        return find((s) => s.toLowerCase() === 'roof');
    }
    return find((s) => s.toLowerCase() === pt);
}

function offerSortKey(productType: string): number {
    const order = ['Solar', 'Roof', 'Battery', 'RR', 'RRR'];
    const matched = order.findIndex(
        (p) => p.toLowerCase() === productType.toLowerCase() || productType.toLowerCase().includes(p.toLowerCase()),
    );
    return matched >= 0 ? matched : 99;
}

export function parsePreContractPricingOffers(
    apiData: any,
    selectedProducts: string[] = [],
): PreContractPricingOffer[] {
    const raw = (apiData?.data ?? apiData) as Array<{
        FinancePartner?: string;
        pricing?: Array<{
            ProductType?: string;
            FinanceMethod?: string;
            financeMethodName?: string;
            Offer?: any[];
        }>;
    }>;
    if (!Array.isArray(raw)) return [];

    const selected =
        selectedProducts.length > 0
            ? selectedProducts
            : (['Solar'] as string[]);

    const offers: PreContractPricingOffer[] = [];
    raw.forEach((block) => {
        const partner = normalizePartner(block.FinancePartner || '');
        const partnerNorm = partner.toLowerCase();
        (block.pricing || []).forEach((pricingItem) => {
            const productTypeRaw = String(pricingItem.ProductType || '').trim() || 'Solar';
            const matchedProduct = matchSelectedProduct(productTypeRaw, selected);
            // Only surface offers for products the customer selected.
            if (!matchedProduct) return;

            const recommended = chooseRecommendedPricingOffer(pricingItem.Offer || []);
            if (!recommended) return;
            if (recommended.__error) {
                offers.push({
                    id: `${partner}-${matchedProduct}-err`,
                    partner,
                    productType: matchedProduct,
                    financeMethod: String(pricingItem.financeMethodName || pricingItem.FinanceMethod || ''),
                    monthly: 0,
                    isCash: partner === 'Cash',
                    term: null,
                    apr: null,
                    error: String(recommended.__error),
                });
                return;
            }
            const isCash = partner === 'Cash' || (pricingItem.FinanceMethod || '').toUpperCase() === 'CASH';
            const monthly = isCash
                ? (typeof recommended.totalCost === 'number' ? recommended.totalCost : 0)
                : monthlyFromOffer(recommended, partnerNorm);
            const { term, apr } = termAprFromOffer(recommended);
            offers.push({
                id: `${partner}-${matchedProduct}-${pricingItem.FinanceMethod || 'offer'}`,
                partner,
                productType: matchedProduct,
                financeMethod: String(pricingItem.financeMethodName || pricingItem.FinanceMethod || ''),
                monthly: Number(monthly) || 0,
                isCash,
                term,
                apr,
            });
        });
    });

    // One recommended offer per partner × selected product (prefer Solar-shaped ids already matched).
    const byPartnerProduct = new Map<string, PreContractPricingOffer>();
    offers.forEach((offer) => {
        if (offer.error) return;
        const key = `${offer.partner}::${offer.productType}`;
        const existing = byPartnerProduct.get(key);
        if (!existing || (existing.monthly <= 0 && offer.monthly > 0)) {
            byPartnerProduct.set(key, offer);
        }
    });

    return Array.from(byPartnerProduct.values()).sort((a, b) => {
        const partnerCmp = a.partner.localeCompare(b.partner);
        if (partnerCmp !== 0) return partnerCmp;
        return offerSortKey(a.productType) - offerSortKey(b.productType);
    });
}

export function buildPreContractPartnerPricingCards(
    offers: PreContractPricingOffer[],
    selectedProducts: string[],
): PreContractPartnerPricingCard[] {
    const selected = selectedProducts.length > 0 ? selectedProducts : ['Solar'];
    const byPartner = new Map<string, PreContractPricingOffer[]>();

    offers.forEach((offer) => {
        if (offer.error) return;
        const list = byPartner.get(offer.partner) ?? [];
        list.push(offer);
        byPartner.set(offer.partner, list);
    });

    const cards: PreContractPartnerPricingCard[] = [];
    byPartner.forEach((lines, partner) => {
        const sorted = [...lines].sort(
            (a, b) => offerSortKey(a.productType) - offerSortKey(b.productType),
        );
        const pricedTypes = new Set(sorted.map((l) => l.productType));
        const missingProducts = selected.filter((p) => !pricedTypes.has(p));
        const isCash = sorted.every((l) => l.isCash) || partner === 'Cash';
        const total = sorted.reduce((sum, line) => sum + (Number(line.monthly) || 0), 0);
        cards.push({
            partner,
            lines: sorted,
            total,
            isCash,
            missingProducts,
        });
    });

    return cards.sort((a, b) => {
        if (a.total > 0 && b.total <= 0) return -1;
        if (b.total > 0 && a.total <= 0) return 1;
        return a.partner.localeCompare(b.partner);
    });
}

export function pickRecommendedOfferForProduct(
    offers: PreContractPricingOffer[],
    product: 'Roof' | 'Battery',
): PreContractPricingOffer | null {
    const candidates = offers.filter(
        (offer) => offer.productType === product && !offer.error && offer.monthly > 0,
    );
    if (candidates.length === 0) return null;
    return candidates.reduce((best, offer) => (offer.monthly < best.monthly ? offer : best));
}

/** All Pricing API offers for selected Roof and/or Battery products. */
export async function fetchRoofBatteryPricingOffers(
    form: PreContractIntakeForm,
): Promise<PreContractPricingOffer[]> {
    const selectedProducts = (['Roof', 'Battery'] as const).filter((product) => form.products[product]);
    if (selectedProducts.length === 0) return [];

    const payload = buildPreContractPricingPayload(form, 'roofBattery');
    if (!payload) {
        const status = getPreContractPricingInputStatus(form, 'roofBattery');
        throw new Error(
            status.missing.length > 0
                ? `Missing pricing inputs: ${status.missing.join(', ')}`
                : 'Roof and battery pricing inputs are incomplete.',
        );
    }

    const res = await quoteServices.getPricing(payload);
    return parsePreContractPricingOffers(res?.data, selectedProducts);
}

/** Pricing API offers for Roof and Battery only — lowest monthly payment per product. */
export async function fetchRoofBatteryPartnerRecommendations(
    form: PreContractIntakeForm,
): Promise<PreContractPricingOffer[]> {
    const offers = await fetchRoofBatteryPricingOffers(form);
    const selectedProducts = (['Roof', 'Battery'] as const).filter((product) => form.products[product]);
    const recommended: PreContractPricingOffer[] = [];
    for (const product of selectedProducts) {
        const best = pickRecommendedOfferForProduct(offers, product);
        if (best) recommended.push(best);
    }
    return recommended;
}

export async function fetchPreContractPricing(
    form: PreContractIntakeForm,
): Promise<PreContractPricingResult> {
    const status = getPreContractPricingInputStatus(form);
    const payload = buildPreContractPricingPayload(form);
    if (!payload) {
        throw new Error(
            status.missing.length > 0
                ? `Missing Pricing inputs: ${status.missing.join(', ')}`
                : 'Pricing inputs are incomplete.',
        );
    }
    const selectedProducts = activeProducts(form);
    const res = await quoteServices.getPricing(payload);
    const offers = parsePreContractPricingOffers(res?.data, selectedProducts);
    return {
        payload,
        selectedProducts,
        offers,
        partnerCards: buildPreContractPartnerPricingCards(offers, selectedProducts),
    };
}
